import { FC } from 'react';
import {
  Autocomplete,
  Box,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import FormRow from '../../components/FormRow';
import Select from '../../components/Select';
import DatePicker from '../../components/DatePicker';
import { ON_PRIMARY_FIELD_SX } from '../../components/fieldStyles';
import { colors, fontWeights } from '../../theme/tokens';
import PageViewer from './PageViewer';
import { Option } from './useMetadataOptions';
import { DocumentPage, MetadataErrors, MetadataValues, QrSummary } from './types';

// Step 2 — "Saisir les données" (XD screen 4). Left column, the
// form with the labels on the left (above on mobile): Titre du
// document, Date d'émission, Service, Émetteur, Confidentiel. Right
// column, the page navigator (PageViewer).
//
// "Émetteur": the XD draws a dropdown; the API stores a string that can be
// the id of a user or free text (it is what the previous forms did
// with their "Zone libre"). A free Autocomplete covers both cases
// with a single control: the accounts of the chosen service as suggestions, and
// any other name typed as-is. A USER does not receive the accounts
// list (requireAdmin), and for them it is a text field.
//
// Required (the XD does not mark them): Titre, Date d'émission and Émetteur (see
// validateMetadata).

type Props = {
  values: MetadataValues;
  errors: MetadataErrors;
  onChange: (patch: Partial<MetadataValues>) => void;
  serviceOptions: Option[];
  issuerOptions: Option[];
  pages: DocumentPage[];
  /** Without it, the viewer does not show the QR state (archived document without pages). */
  qr?: QrSummary;
  pageIndex: number;
  onPageIndexChange: (index: number) => void;
  disabled?: boolean;
};

const Step2Metadata: FC<Props> = ({
  values,
  errors,
  onChange,
  serviceOptions,
  issuerOptions,
  pages,
  qr,
  pageIndex,
  onPageIndexChange,
  disabled = false,
}) => {
  const issuerSelected =
    issuerOptions.find((option) => option.value === values.issuer) ?? null;

  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)',
        columnGap: 4,
        rowGap: 3,
        [theme.breakpoints.down('tablet')]: {
          gridTemplateColumns: 'minmax(0, 1fr)',
        },
      })}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <FormRow label="Titre du document" htmlFor="document-title" error={errors.title}>
          <TextField
            id="document-title"
            name="title"
            size="small"
            fullWidth
            value={values.title}
            onChange={(e) => onChange({ title: e.target.value })}
            error={Boolean(errors.title)}
            placeholder="Nom du document saisi"
            disabled={disabled}
            sx={ON_PRIMARY_FIELD_SX}
          />
        </FormRow>

        <FormRow label="Date d'émission" htmlFor="document-issuing-date" error={errors.issuingDate}>
          <DatePicker
            value={values.issuingDate}
            onChange={(date: Dayjs | null) => onChange({ issuingDate: date })}
            disabled={disabled}
            disableFuture
            slotProps={{
              textField: {
                id: 'document-issuing-date',
                name: 'issuing_date',
                size: 'small',
                fullWidth: true,
                placeholder: 'JJ/MM/AAAA',
                error: Boolean(errors.issuingDate),
                sx: ON_PRIMARY_FIELD_SX,
              },
            }}
          />
        </FormRow>

        <FormRow label="Service" htmlFor="document-service">
          <Select
            id="document-service"
            name="service"
            size="small"
            fullWidth
            displayEmpty
            value={values.service}
            onChange={(e) => onChange({ service: e.target.value as string })}
            disabled={disabled}
            renderValue={(selected: unknown) => {
              const found = serviceOptions.find((option) => option.value === selected);
              return found ? (
                found.label
              ) : (
                <span style={{ color: colors.hint }}>Sélectionnez le service</span>
              );
            }}
            inputProps={{ 'aria-label': 'Service' }}
            sx={ON_PRIMARY_FIELD_SX}
          >
            {[
              <MenuItem key="__none" value="">
                <em>Aucun</em>
              </MenuItem>,
              ...serviceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              )),
            ]}
          </Select>
        </FormRow>

        <FormRow label="Émetteur" htmlFor="document-issuer" error={errors.issuer}>
          <Autocomplete
            freeSolo
            // `freeSolo` hides the dropdown arrow, but the XD draws this
            // field as a dropdown: keep the arrow whenever there are
            // suggestions to open. A USER gets no accounts list and for
            // them it stays a plain text field, without the arrow.
            forcePopupIcon={issuerOptions.length > 0}
            id="document-issuer"
            options={issuerOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={issuerSelected ?? (values.issuer || null)}
            inputValue={issuerSelected ? issuerSelected.label : values.issuer}
            onChange={(_, value) =>
              onChange({
                issuer:
                  value === null ? '' : typeof value === 'string' ? value : value.value,
              })
            }
            onInputChange={(_, text, reason) => {
              // Typing stores the text; choosing a suggestion, the
              // id arrives via `onChange` and the text must not overwrite it.
              if (reason === 'input') onChange({ issuer: text });
              if (reason === 'clear') onChange({ issuer: '' });
            }}
            disabled={disabled}
            noOptionsText="Aucune suggestion"
            renderInput={(params) => (
              <TextField
                {...params}
                name="issuer"
                size="small"
                error={Boolean(errors.issuer)}
                placeholder={
                  issuerOptions.length > 0
                    ? "Sélectionnez l'émetteur"
                    : "Saisissez l'émetteur"
                }
                sx={ON_PRIMARY_FIELD_SX}
              />
            )}
          />
        </FormRow>

        <FormRow label="Confidentiel" htmlFor="document-confidential">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              id="document-confidential"
              name="confidential"
              color="secondary"
              checked={values.confidential}
              onChange={(e) => onChange({ confidential: e.target.checked })}
              disabled={disabled}
              sx={{
                '& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb': {
                  backgroundColor: colors.white,
                },
                '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': {
                  backgroundColor: colors.white,
                  opacity: 0.5,
                },
                // On: white thumb over a solid orange track. The default
                // secondary track is faded and reads as off on the blue modal.
                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                  backgroundColor: colors.white,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: colors.accent,
                  opacity: 1,
                },
              }}
            />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: fontWeights.regular }}>
              {values.confidential ? 'Oui' : 'Non'}
            </Typography>
          </Box>
        </FormRow>
      </Box>

      {/* S5 draws no preview on mobile: the viewer is desktop-only. */}
      <Box
        sx={(theme) => ({
          [theme.breakpoints.down('tablet')]: { display: 'none' },
        })}
      >
        <PageViewer
          pages={pages}
          qr={qr}
          index={pageIndex}
          onIndexChange={onPageIndexChange}
        />
      </Box>
    </Box>
  );
};

export default Step2Metadata;
